import React from 'react'
import PropTypes from 'prop-types'

import { orange } from '@mui/material/colors'
import { Box, Typography, Stack, Chip, Divider } from '@mui/material'
import Grid from '@mui/material/Grid'

import { getInvoiceIsPartialStatusAndBucket } from 'utils/agingBucket'
import { formatDateToFEFormat } from 'utils/date'
import { AGING_BUCKET_COLORS } from 'components/common/aging-bucket/settings'
import { PAYMENT_METHODS, PAYMENT_METHOD_IMAGES } from 'settings/constants/payment'
import { INVOICE_TYPE } from 'settings/constants/billing'
import { deriveInvoicePeriodLabel } from 'data/billing-period/invoicePeriodLabel'

import T from 'T'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import InvoiceStatusChip from 'components/common/chip/InvoiceStatusChip'
import { TOTAL_COLUMN_WIDTH, AMOUNT_APPLIED_COLUMN_WIDTH } from './settings'

const { ACCOUNT_CREDIT } = PAYMENT_METHODS
const { MANUAL } = INVOICE_TYPE

const InvoiceActivity = ({ chipGridWidth = 100, invoice, showAppliedColumn = false, align = 'center', marginY = 0 }) => {
  const transformDate = date => (date ? formatDateToFEFormat(date) : '-')

  const {
    invoiceNumber,
    invoiceDate,
    dueDate,
    amountPaidCents,
    totalCents,
    status,
    startDate,
    endDate,
    type,
    paymentAppliedAmountCents,
    latestBatchStatus,
    latestBatchFailure,
  } = invoice || {}
  const pendingAmount = totalCents - amountPaidCents
  const isAccountCreditType = invoice?.isAccountCredit
  const { label: invoicePeriodLabel } = deriveInvoicePeriodLabel({ startDate, endDate, invoiceDate, type })
  const title = isAccountCreditType ? invoice?.title : invoicePeriodLabel
  const { isPartial, agingBucketType } = getInvoiceIsPartialStatusAndBucket(status, dueDate, amountPaidCents, totalCents)

  return (
    <Grid container spacing={2.5} wrap="nowrap" alignItems={align} size={12}>
      <Grid minWidth={chipGridWidth} my={marginY} size="auto">
        <Stack alignItems="flex-start" spacing={0.5}>
          <InvoiceStatusChip
            status={status}
            isPartial={isPartial}
            agingBucketType={agingBucketType}
            generationStatus={latestBatchStatus}
            generationFailure={latestBatchFailure}
          />
          {isPartial && agingBucketType && (
            <Typography color={AGING_BUCKET_COLORS[agingBucketType][700]} variant="caption" fontWeight={700}>
              <HHDisplayMoney value={pendingAmount} /> left
            </Typography>
          )}
        </Stack>
      </Grid>
      <Grid my={marginY} size="grow">
        {!isAccountCreditType && (
          <>
            <Typography variant="h6" fontWeight={500} color="textPrimary">
              {title}
              {type === MANUAL && <Chip label={T.MANUAL} size="small" sx={{ backgroundColor: orange[100], ml: 1, borderRadius: 1 }} />}
            </Typography>
            <Typography variant="caption">
              Invoice #: {invoiceNumber} // Invoice date: {transformDate(invoiceDate)}
            </Typography>
          </>
        )}
        {isAccountCreditType && (
          <>
            <Box display="flex" alignItems="center">
              <img src={PAYMENT_METHOD_IMAGES[ACCOUNT_CREDIT]} alt="" />
              <Stack ml={1}>
                <Typography display="inline" variant="h6" fontWeight={500}>
                  {title}
                </Typography>
              </Stack>
            </Box>
          </>
        )}
      </Grid>
      <Grid width={TOTAL_COLUMN_WIDTH} my={marginY}>
        <Typography display="flex" alignItems="center" justifyContent="flex-end" variant="h6" fontWeight={500} color="textPrimary">
          <HHDisplayMoney value={totalCents} />
        </Typography>
        {!isAccountCreditType && (
          <Typography display="flex" alignItems="center" justifyContent="flex-end" variant="caption">
            Due: {transformDate(dueDate)}
          </Typography>
        )}
      </Grid>
      {showAppliedColumn && (
        <>
          <Grid display="flex" size="auto">
            <Divider flexItem orientation="vertical" />
          </Grid>
          <Grid width={AMOUNT_APPLIED_COLUMN_WIDTH} my={marginY}>
            <Typography display="flex" alignItems="center" justifyContent="flex-end" variant="h6" fontWeight={500} color="textPrimary">
              <HHDisplayMoney value={paymentAppliedAmountCents} />
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  )
}

InvoiceActivity.propTypes = {
  chipGridWidth: PropTypes.number,
  invoice: PropTypes.object.isRequired,
  showAppliedColumn: PropTypes.bool,
  align: PropTypes.string,
  marginY: PropTypes.string,
}

export default InvoiceActivity
